import { useMemo } from "react";
import { useAuthenticated } from "../auth";
import {
  portalWmsResourceParams,
  portalWmsTileUrl,
} from "../components/map/portal-wms";
import {
  countPrecacheTiles,
  getPrecacheCoords,
  range,
} from "../components/map/tile-coords";
import { normalizeOfflineArea } from "../license/offline-area";
import { logger } from "../log";
import {
  type SyncModule,
  useSyncData,
  useSyncModuleRegistration,
} from "../sync";
import { usePortalApi } from "./portal-api";
import { daily } from "./provider";
import { precacheResources, type ResourceParams } from "./resource";

const log = logger("map-layers");
const syncModuleId = "map-layers";
const precacheSyncModuleId = `${syncModuleId}:precache`;
const tilesSyncModuleId = `${syncModuleId}:tiles`;

// WMS tiles go through the portal's WMS proxy and the (often slow) upstream
// service, so only the zoom levels needed for an overview are precached;
// closer zoom levels are scaled up offline. Large offline areas with several
// sub-layers quickly reach hundreds of thousands of tiles — the finest zoom
// levels are dropped until the total stays below the limit.
const wmsMinZoom = 14;
const wmsMaxZoom = 17;
const maxWmsTiles = 30_000;

export type PortalMapLayerFile = {
  readonly id: string;
  readonly originalName: string;
  readonly mimeType: string;
  readonly size: number;
};

export type PortalWMSSubLayer = {
  readonly name: string;
  readonly title?: string;
  readonly opacity?: number;
};

export type PortalMapLayerBase = {
  readonly id: string;
  readonly name: string;
  readonly minZoom?: number;
  readonly maxZoom?: number;
};

export type PortalWMSMapLayer = PortalMapLayerBase & {
  readonly type: "WMS";
  readonly url: string;
  readonly wmsUsername?: string;
  readonly wmsPassword?: string;
  readonly wmsLayers: ReadonlyArray<PortalWMSSubLayer>;
};

export type PortalShapeMapLayer = PortalMapLayerBase & {
  readonly type: "SHAPE";
  readonly file: PortalMapLayerFile;
  /** Color for lines, areas and points (#RRGGBB) set in the Datenportal; null = Leaflet default */
  readonly color?: string | null;
  /** Color per layer inside the ZIP (key = shpjs fileName); layers not listed use `color` */
  readonly layerColors?: Readonly<Record<string, string>> | null;
};

export type PortalMapLayer = PortalWMSMapLayer | PortalShapeMapLayer;

function usePortalMapLayersSyncModule(): SyncModule<
  ReadonlyArray<PortalMapLayer>
> {
  const { portalKey } = useAuthenticated();
  const { useNewApi, baseUrl } = usePortalApi();

  return useMemo(
    () => ({
      id: syncModuleId,
      i18nKey: `karte:tasks.${syncModuleId}`,
      defaultOptions: { schedule: { type: "periodic", interval: daily } },
      // Custom WMS/shape map layers are a new-Datenportal-only resource — the
      // legacy portal has no equivalent endpoint, so we simply don't fetch
      // anything for it. The legacy portal continues to serve its custom
      // layers via `license.mapLayers` instead (see `../license`).
      fetchData:
        useNewApi && baseUrl
          ? () => fetchPortalMapLayers(portalKey, baseUrl)
          : async () => [],
    }),
    [portalKey, useNewApi, baseUrl],
  );
}

async function fetchPortalMapLayers(
  portalKey: string,
  baseUrl: string,
): Promise<ReadonlyArray<PortalMapLayer>> {
  const response = await fetch(`${baseUrl}/api/device/sync/map-layers`, {
    headers: { Authorization: `Bearer ${portalKey}` },
  });

  if (!response.ok) {
    throw new Error(
      `Error fetching data: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json();
  const mapLayers: PortalMapLayer[] = data.mapLayers ?? [];

  return mapLayers.flatMap((layer): PortalMapLayer[] => {
    if (layer.type === "SHAPE") {
      if (layer.file) return [layer];
      log.warn(`Ignoriere Shape-Layer "${layer.id}" ohne Datei`);
      return [];
    }
    if (layer.type !== "WMS") return [layer];
    if (!isAbsoluteHttpUrl(layer.url)) {
      log.warn(
        `Ignoriere WMS-Layer "${layer.id}" mit ungültiger URL: ${layer.url}`,
      );
      return [];
    }
    // The portal sends `wmsLayers: null` when no sub-layers are configured.
    return [{ ...layer, wmsLayers: layer.wmsLayers ?? [] }];
  });
}

function isAbsoluteHttpUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

export function useMapLayersSync() {
  const module = usePortalMapLayersSyncModule();
  useSyncModuleRegistration(module);
  const precacheModule = usePortalMapLayerFilesPrecacheSyncModule();
  useSyncModuleRegistration(precacheModule);
  const tilesModule = usePortalWmsTilesPrecacheSyncModule();
  useSyncModuleRegistration(tilesModule);
}

export function usePortalMapLayers(): ReadonlyArray<PortalMapLayer> {
  return useSyncData(syncModuleId) ?? [];
}

// Shape files are downloaded ahead of time so custom layers also work offline.
function usePortalMapLayerFilesPrecacheSyncModule(): SyncModule<void> {
  const { portalKey } = useAuthenticated();
  const { useNewApi, baseUrl } = usePortalApi();

  return useMemo(
    () => ({
      id: precacheSyncModuleId,
      i18nKey: `karte:tasks.${syncModuleId}.precache`,
      defaultOptions: { schedule: { type: "periodic", interval: daily } },
      dependsOn: [syncModuleId],
      fetchData: async ({ dataProvider, observer }) => {
        if (!useNewApi || !baseUrl) return;
        const mapLayers =
          dataProvider.getModuleData<ReadonlyArray<PortalMapLayer>>(
            syncModuleId,
          ) ?? [];
        const urls = mapLayers
          .filter((l) => l.type === "SHAPE")
          .map((l) => portalMapLayerFileUrl(baseUrl, l.id));

        await precacheResources(
          urls,
          portalMapLayerFileResourceParams(portalKey),
          observer,
          { skipFailures: true },
        );
      },
    }),
    [portalKey, useNewApi, baseUrl],
  );
}

// WMS tiles for the license's offline area. Independent of the map module's
// OSM precache ("map-tiles"), so custom layers are also available offline on
// licenses without the "kartenmaterial" module.
function usePortalWmsTilesPrecacheSyncModule(): SyncModule<void> {
  const { portalKey, offlineArea } = useAuthenticated();
  const { useNewApi, baseUrl } = usePortalApi();

  return useMemo(
    () => ({
      id: tilesSyncModuleId,
      i18nKey: `karte:tasks.${syncModuleId}.tiles`,
      defaultOptions: {
        schedule: { type: "periodic", interval: daily },
        requiredNetworkStatus: ["wifi"],
      },
      dependsOn: [syncModuleId],
      fetchData: async ({ dataProvider, observer }) => {
        const polygon = normalizeOfflineArea(offlineArea);
        if (!useNewApi || !baseUrl || !polygon) return;

        const mapLayers =
          dataProvider.getModuleData<ReadonlyArray<PortalMapLayer>>(
            syncModuleId,
          ) ?? [];

        const wmsLayers = mapLayers.filter(
          (layer): layer is PortalWMSMapLayer => layer.type === "WMS",
        );
        const zoomsFor = (layer: PortalWMSMapLayer, maxZoom: number) =>
          range(wmsMinZoom, maxZoom).filter(
            (z) =>
              (layer.minZoom == null || z >= layer.minZoom) &&
              (layer.maxZoom == null || z <= layer.maxZoom),
          );
        const tileCount = (maxZoom: number) =>
          wmsLayers.reduce(
            (sum, layer) =>
              sum +
              countPrecacheTiles(polygon, zoomsFor(layer, maxZoom)) *
                layer.wmsLayers.length,
            0,
          );

        let maxZoom = wmsMaxZoom;
        while (maxZoom >= wmsMinZoom && tileCount(maxZoom) > maxWmsTiles) {
          maxZoom--;
        }
        if (maxZoom < wmsMinZoom) {
          log.warn(
            `Offline area too large for WMS precaching (${tileCount(wmsMinZoom)} tiles at zoom ${wmsMinZoom}, limit ${maxWmsTiles}) — skipped`,
          );
          return;
        }
        if (maxZoom < wmsMaxZoom) {
          log.info(
            `WMS precaching limited to zoom ${wmsMinZoom}–${maxZoom} (${tileCount(wmsMaxZoom)} tiles up to zoom ${wmsMaxZoom} exceed the limit of ${maxWmsTiles})`,
          );
        }

        const urls = wmsLayers.flatMap((layer) => {
          const coords = getPrecacheCoords(polygon, zoomsFor(layer, maxZoom));
          return layer.wmsLayers.flatMap((subLayer) =>
            coords.map((c) =>
              portalWmsTileUrl(baseUrl, layer.id, subLayer.name, c),
            ),
          );
        });

        log.info(`Precaching ${urls.length} WMS tiles…`);
        await precacheResources(
          urls,
          portalWmsResourceParams(portalKey),
          observer,
          { skipFailures: true },
        );
      },
    }),
    [portalKey, offlineArea, useNewApi, baseUrl],
  );
}

function portalMapLayerFileUrl(baseUrl: string, mapLayerId: string): string {
  return `${baseUrl}/api/device/sync/map-layers/${encodeURIComponent(mapLayerId)}/file/download`;
}

function portalMapLayerFileResourceParams(portalKey: string): ResourceParams {
  return {
    requestOptions: { headers: { Authorization: `Bearer ${portalKey}` } },
  };
}

export function usePortalMapLayerFileDownload(mapLayerId: string): {
  readonly url: string;
  readonly params: ResourceParams;
} {
  const { portalKey } = useAuthenticated();
  const { baseUrl } = usePortalApi();

  return useMemo(
    () => ({
      url: portalMapLayerFileUrl(baseUrl ?? "", mapLayerId),
      params: portalMapLayerFileResourceParams(portalKey),
    }),
    [baseUrl, mapLayerId, portalKey],
  );
}
